import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, fmtDate, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ClipboardList, ShoppingBag, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  pending: "secondary", confirmed: "outline", packed: "outline", ready: "outline",
  assigned: "outline", out_for_delivery: "outline", delivered: "default",
  cancelled: "destructive", returned: "destructive", refunded: "destructive",
};

export default function MyOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/me?limit=50")
      .then((r) => setRows(r.data.items || []))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="my-orders-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-display font-semibold">My orders</h1>
        <p className="text-sm text-muted-foreground mt-1">Everything you&rsquo;ve bought from Babu Baazar.</p>
      </div>

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card className="card-soft border-transparent" data-testid="my-orders-empty">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-accent/10 text-accent grid place-items-center mx-auto mb-4">
              <ClipboardList className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-display font-semibold">No orders yet</h2>
            <p className="text-sm text-muted-foreground mt-1">Your first delivery from Babu Store is just a tap away.</p>
            <Button asChild className="mt-5 btn-primary-glow" size="lg">
              <Link to="/shop"><ShoppingBag className="w-4 h-4 mr-2" />Start shopping</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && rows.map((o) => (
        <Link key={o.id} to={`/account/orders/${o.id}`} data-testid={`order-row-${o.order_number}`}>
          <Card className="card-soft border-transparent mb-3 hover:shadow-md transition">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary grid place-items-center shrink-0">
                <ClipboardList className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono font-semibold">{o.order_number}</div>
                  <Badge variant={STATUS_TONE[o.status] || "secondary"} className="capitalize text-[10px]">{o.status?.replace(/_/g, " ")}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(o.created_at)} · {o.items?.length} item{(o.items?.length || 0) > 1 ? "s" : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold">{money(o.pricing?.total)}</div>
                <div className="text-xs uppercase text-muted-foreground">{o.payment?.method}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}
